// THE CONJURE SPELLBOOK — R216 EXTRACTED IT FROM `src/App.tsx`, AND THE DEFECT IS WHY.
//
// This room re-reads `/api/spells` every time a member taps one of THIRTEEN categories, and it had
// no sequencing at all — so ordering alone broke both halves of R70's law. A stale refusal printed
// "we could not open it" over a category sitting right there; a stale success painted one category's
// spells under another's active tab. Neither has any symptom but the wrong words on her screen.
//
// **AND `loadSpells` HAS TWO CALLERS, WHICH IS WHY NO CLEANUP FLAG COULD HAVE FIXED IT.** The
// category effect is one; the refresh fired after a generation is delivered is the other, and it
// runs right after `turnTo(sp)` opens the working she has just PAID for. A stale list landing
// behind that leaves her new spell out of the index and both page arrows dead on it. React's
// cleanup only ever reaches reads its own effect started; the counter in `src/lib/latest-read.ts`
// reaches both.
//
// Eleven hooks, so a gate cannot CALL this the way `r7-components.mjs` calls a leaf — the reach it
// buys is the IMPORT (`SignInModal`'s precedent, L10), and that is what lets `r216-latest-read.mjs`
// read this room's real guard instead of a regex over twelve thousand lines.
import { FormEvent, useEffect, useRef, useState } from "react";
import { BookOpen, Check, ChevronLeft, ChevronRight, Feather } from "lucide-react";
import { listSpells, spellBrowseNote } from "../lib/spell-browse";
import { beginRead, readMayPaint } from "../lib/latest-read";
import { spellKicker, spellToReport } from "../lib/spell-report";
import { downloadReportPdf } from "../lib/downloads";
import { postReadingToRoom, saveReadingNote, saveReadingState } from "../lib/saved-readings";
import { catalogSealNote } from "../catalogSeals.js";
import { cleanCreationName, MAX_CREATION_NAME } from "../creationName.js";
import { roomLabel } from "../rooms.js";
import { saveCopy } from "../saveCopy.js";
import { t } from "../strings.js";
import { SPELLBOOK_CATEGORIES } from "../catalogs/spellbook";
import { SendToCauldronButton } from "./SendToCauldronButton";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";
import type { LockInfo } from "../lib/tier-catalog";
import magicTileSpellbookUrl from "../assets/magic-tile-spellbook.webp";
import magicBookOpenUrl from "../assets/magic-book-open.webp";

// Every spell is grounded strictly in Lala's own teachings (server-side), so nothing is invented.
// T7-3: a catalog recipe (her own candle sheet) rides in on the same shape, carrying its seals AS
// IMAGES, its working as structured step cards, and her layering teaching. A generated spell has
// none of these fields and renders exactly as it always did.
type CatalogSeal = {
  kind: "pentacle" | "spirit" | "unresolved" | "unavailable";
  text: string; name?: string; meaning?: string; imageUrl?: string; herNote?: string; alternative?: boolean;
  sealOfGod?: { name: string; imageUrl: string; placement?: string };
};
type SpellStepCard = { text: string; sealName?: string; sealImageUrl?: string; sealOfGodName?: string; sealOfGodImageUrl?: string };
export type SpellLayering = { principle?: string; oilAlone?: string; layered?: string; alternative?: string };
type SpellEntry = {
  id: string; slug: string; title: string; category: string; purpose: string;
  herbs: string[]; tools: string[]; oils: string[]; steps: string[]; source?: string;
  seals?: CatalogSeal[]; stepCards?: SpellStepCard[]; layering?: SpellLayering | null;
};
// R6-4: SPELLBOOK_CATEGORIES moved VERBATIM to `src/catalogs/spellbook.ts`.

// MR-SB2 — THE ANIMATED SPELLBOOK. The closed book grows toward the screen and opens; the left page is
// the contents (categories + make-a-spell + the entry list), the right page is the open spell. Choosing
// a spell turns the page to it; prev/next turn through the list. Luxury occult, smooth on desktop and
// mobile, and a graceful reduced-motion fallback (instant swaps, no 3D).
export function SpellbookExperience({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const reduced = usePrefersReducedMotion();
  // MR-L2.5: one click from the scene opens the book straight into the grow-and-open animation; there is
  // no intermediate closed-book step. The book mounts already open, so `sb-openin` plays on entry.
  const [open, setOpen] = useState(true);
  const [category, setCategory] = useState("");
  const [situation, setSituation] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  // R70: NULL IS NOT AN EMPTY BOOK — W5-2/R39's third state. "We could not open it" and "this
  // category has nothing in it yet" are two facts and the page prints a different sentence for each.
  const [browse, setBrowse] = useState<SpellEntry[] | null>(null);
  const [openSpell, setOpenSpell] = useState<SpellEntry | null>(null);
  const [pageKey, setPageKey] = useState(0);
  const [mobilePage, setMobilePage] = useState<"contents" | "page">("contents");
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  // N7-4: what SHE calls the spell she is about to have written. Asked once on the make-a-spell page.
  const [creationName, setCreationName] = useState("");
  // The name she gave, pinned to the spell it was given FOR. A curated spell she browses to afterwards
  // keeps its own title from the teachings; her name never leaks onto someone else's spell.
  const [namedSpell, setNamedSpell] = useState<{ id: string; name: string } | null>(null);
  // T8-2: the server tells this book whether the member may READ what it lists. It sends titles
  // either way, so the shelf still shows what is inside; `locked` is what decides whether a tap
  // opens the recipe or her box. The client never decides this — it is told.
  const [spellsLocked, setSpellsLocked] = useState(false);
  const [spellLock, setSpellLock] = useState<LockInfo | null>(null);

  // R70: ONE read of this route, so the post-generation refresh cannot drift from the mount's (R31).
  // A refusal NEVER paints — an unread book is not an empty one — and it always SAYS SO. `locked` is
  // only ever moved by an answer that landed: the client is TOLD about that door (T8-2) and must not
  // be told the open answer by a catch block (R20).
  const [browseNote, setBrowseNote] = useState("");
  // R216: ONLY THE NEWEST READ MAY PAINT. Thirteen categories are one tap apart, so two reads are
  // routinely in flight — and with no sequencing, ordering alone breaks BOTH halves of R70's law:
  // a stale refusal prints "we could not open it" over a category sitting right there, and a stale
  // success paints one category's spells under another's active tab. The counter is on a REF and
  // never on state (D-5), and it guards `loadSpells`'s SECOND caller too — the post-generation
  // refresh, which no React cleanup could ever have reached.
  const browseSeq = useRef(0);
  async function loadSpells() {
    const ticket = beginRead(browseSeq);
    const answer = await listSpells<SpellEntry>(category);
    // A SUPERSEDED READ IS DROPPED WHOLE — not the list, not the lock, not the note. Painting three
    // of the four is worse than painting none: a stale note under a fresh index is the same lie one
    // line down (ITEM 10's own words on the money path).
    if (!readMayPaint(browseSeq, ticket)) return;
    if (answer.ok) {
      setBrowse(answer.spells);
      setSpellsLocked(Boolean(answer.locked));
      setSpellLock(answer.locked ? (answer.lock as LockInfo) : null);
    } else setBrowse(null);
    setBrowseNote(spellBrowseNote(answer, t(language, "spell.browseFailed")));
  }
  useEffect(() => { void loadSpells(); }, [category]);

  const turnTo = (s: SpellEntry | null) => {
    // T8-2/T8-3: in the Magic Room the CONTENT is the product. A member without the room may see
    // that a spell exists; touching it raises HER BOX, fed by the server's own lock payload, rather
    // than opening a recipe she has not paid for or showing her a bare line of text.
    if (s && spellsLocked) { onRequireMembership(() => {}, spellLock || undefined); return; }
    setOpenSpell(s);
    setPageKey((k) => k + 1);
    setSave("idle"); setSaveNote("");
    setMobilePage("page");
  };

  // R70: the page turners walk the list the server really sent. An unread book has no next page.
  const pages = browse || [];
  const idx = openSpell ? pages.findIndex((b) => b.id === openSpell.id) : -1;
  const goPrev = () => { if (idx > 0) turnTo(pages[idx - 1]); };
  const goNext = () => { if (idx >= 0 && idx < pages.length - 1) turnTo(pages[idx + 1]); };

  function submit(event: FormEvent) {
    event.preventDefault();
    if (situation.trim().length < 6 && !category) { setStatus(t(language, "spell.needSituation")); return; }
    onRequireMembership(() => { void run(); });
  }

  async function run() {
    setBusy(true);
    setStatus(t(language, "spell.stage.teachings"));
    const stages = [t(language, "spell.stage.need"), t(language, "spell.stage.teachings"), t(language, "spell.stage.gathering"), t(language, "spell.stage.writing")];
    let i = 0;
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/spell", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ situation: situation.trim(), category, creationName: cleanCreationName(creationName) }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "spell.signIn")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "spell.failed"));
        return;
      }
      const sp = (data.report as { spell?: SpellEntry }).spell;
      // N7-4: pin her name to THIS spell. The field then clears, so the next spell is asked fresh.
      const given = cleanCreationName(creationName);
      if (sp && given) { setNamedSpell({ id: String(sp.id), name: given }); setCreationName(""); }
      if (sp) turnTo(sp);
      setStatus("");
      void loadSpells();
    } catch {
      setStatus(t(language, "spell.failed"));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  // N7-4: the name for the spell currently open. Hers if she named this one, otherwise the spell's own
  // title from the teachings. Never "You · <title>", which the shelf read back as "<title>, You".
  const spellShelfName = (s: SpellEntry) => (namedSpell && namedSpell.id === String(s.id) ? namedSpell.name : s.title);

  async function saveToRoom() {
    if (!openSpell) return;
    setSave("saving"); setSaveNote("");
    const answer = await postReadingToRoom({ type: "spell", title: spellShelfName(openSpell), data: spellToReport(openSpell) });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  // MR-B2: the two-page spread is her open-book art (landscape). "contents" shows the browse; opening a
  // spell turns to the "spell" spread (left = purpose + ingredients, right = how to work it), so a spell
  // uses BOTH pages like a real book. On mobile one page shows at a time via a side toggle.
  const spread: "contents" | "spell" = openSpell ? "spell" : "contents";
  const showSide = (side: "left" | "right") => mobilePage === "contents" ? side === "left" : side === "right";

  const contentsLeft = (
    <div className="sb-pagebody">
      <p className="sb-page__eyebrow">{t(language, "spell.contents")}</p>
      <div className="sb-cats">
        <button type="button" className={`sb-cat${category === "" ? " is-active" : ""}`} onClick={() => setCategory("")}>{t(language, "spell.all")}</button>
        {SPELLBOOK_CATEGORIES.map((c) => (
          <button type="button" key={c} className={`sb-cat${category === c ? " is-active" : ""}`} onClick={() => setCategory(c)}>{c}</button>
        ))}
      </div>
      <ul className="sb-index" key={category || "all"}>
        {browse === null ? <li className="sb-index__empty">{t(language, "spell.unread")}</li> : browse.length ? browse.map((s) => (
          <li key={s.id}>
            <button type="button" className={`sb-index__item${openSpell?.id === s.id ? " is-current" : ""}`} onClick={() => turnTo(s)}>
              <span className="sb-index__title">{s.title}</span>
              <span className="sb-index__cat">{s.category}</span>
            </button>
          </li>
        )) : <li className="sb-index__empty">{t(language, "spell.emptyCategory")}</li>}
      </ul>
      {/* R70: the book's own answer, at page width and beside the index it is about. */}
      {browseNote ? <p className="sb-index__note" role="status">{browseNote}</p> : null}
    </div>
  );

  const contentsRight = (
    <div className="sb-pagebody sb-pagebody--center">
      <Feather size={24} className="sb-quill" />
      <p className="sb-page__eyebrow">{t(language, "spell.make.eyebrow")}</p>
      <form className="sb-make" onSubmit={submit}>
        <textarea value={situation} onChange={(e) => setSituation(e.target.value)} rows={3}
          placeholder={t(language, "spell.make.placeholder")} />
        {/* N7-4: NAME YOUR CREATION, asked once, optional, taken verbatim. Blank keeps the spell's
            own title from the teachings; it is never that title wearing her personal name. */}
        <input className="sb-make__name creation-name__input" type="text" value={creationName}
          maxLength={MAX_CREATION_NAME} onChange={(e) => setCreationName(e.target.value)}
          placeholder={t(language, "spell.name.placeholder")} aria-label={t(language, "spell.name.aria")} />
        <button className={`button button--primary${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy ? "spell.make.going" : "spell.make.eyebrow")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>
      <p className="sb-hint">{t(language, "spell.hint")}</p>
    </div>
  );

  const spellLeft = openSpell ? (
    <div className="sb-pagebody">
      <p className="sb-spell__kicker">{spellKicker(openSpell, language, t)} · {openSpell.category}</p>
      <h3 className="sb-spell__title">{openSpell.title}</h3>
      <p className="sb-spell__purpose">{openSpell.purpose}</p>
      {/* T7-2/T7-3: her layering, said plainly on every recipe that carries it — the oil is spelled
          first as its own working, and THAT oil goes into the spell. */}
      {openSpell.layering?.principle ? (
        <div className="sb-block sb-block--layering">
          <h4 className="sb-block__label">{t(language, "spell.block.layering")}</h4>
          <p>{openSpell.layering.principle}</p>
          {openSpell.layering.oilAlone || openSpell.layering.layered ? (
            <p>{[openSpell.layering.oilAlone, openSpell.layering.layered].filter(Boolean).join(" ")}</p>
          ) : null}
        </div>
      ) : null}
      {/* T7-3: SEALS ARE SHOWN, not just named. A Greater Keys pentacle brings its Seal of God with
          it (the pairing law); one of the 72 Angels is named and explained but never handed over as
          a seal, and an entry she wrote that we cannot resolve rides through in her own words. */}
      {openSpell.seals?.length ? (
        <div className="sb-block">
          <h4 className="sb-block__label">{t(language, "spell.block.seals")}</h4>
          <ul className="sb-seals">
            {openSpell.seals.map((seal, i) => (
              <li key={i} className="sb-seal">
                {seal.imageUrl ? (
                  <span className="sb-seal__imgs">
                    <img className="sb-seal__img" src={seal.imageUrl} alt={seal.name || seal.text} loading="lazy" />
                    {seal.sealOfGod?.imageUrl ? (
                      <img className="sb-seal__img" src={seal.sealOfGod.imageUrl} alt={seal.sealOfGod.name} loading="lazy" />
                    ) : null}
                  </span>
                ) : null}
                <span className="sb-seal__text">
                  <span className="sb-seal__name">{seal.name || seal.text}{seal.alternative ? ` ${t(language, "spell.seal.or")}` : ""}</span>
                  {seal.herNote ? <span className="sb-seal__note">{t(language, "spell.seal.herNote")} {seal.herNote}</span> : null}
                  {seal.meaning ? <span className="sb-seal__meaning">{seal.meaning}</span> : null}
                  {seal.sealOfGod ? <span className="sb-seal__meaning">{t(language, "spell.seal.with", { name: seal.sealOfGod.name, placement: seal.sealOfGod.placement || "" })}</span> : null}
                  {catalogSealNote(seal) ? <span className="sb-seal__note">{catalogSealNote(seal)}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {openSpell.herbs?.length ? (<div className="sb-block"><h4 className="sb-block__label">{t(language, "spell.block.herbs")}</h4><ul className="sb-block__list">{openSpell.herbs.map((h, i) => <li key={i}>{h}</li>)}</ul></div>) : null}
      {openSpell.tools?.length ? (<div className="sb-block"><h4 className="sb-block__label">{t(language, "spell.block.tools")}</h4><ul className="sb-block__list">{openSpell.tools.map((t, i) => <li key={i}>{t}</li>)}</ul></div>) : null}
      {openSpell.oils?.length ? (<div className="sb-block"><h4 className="sb-block__label">{t(language, "spell.block.oils")}</h4><ul className="sb-block__list">{openSpell.oils.map((o, i) => <li key={i}>{o}</li>)}</ul></div>) : null}
    </div>
  ) : null;

  const spellRight = openSpell ? (
    <div className="sb-pagebody">
      <h4 className="sb-block__label">{t(language, "spell.block.how")}</h4>
      {openSpell.stepCards?.length ? (
        <ol className="working-steps">
          {openSpell.stepCards.map((step, i) => (
            <li key={i} className="working-steps__step">
              <p className="working-steps__text">{step.text}</p>
              {step.sealImageUrl ? (
                <span className="working-steps__seals">
                  <img className="working-steps__thumb" src={step.sealImageUrl} alt={step.sealName || "Seal"} loading="lazy" />
                  {step.sealOfGodImageUrl ? (
                    <img className="working-steps__thumb" src={step.sealOfGodImageUrl} alt={step.sealOfGodName || "Seal of God"} loading="lazy" />
                  ) : null}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <ol className="sb-block__steps">{openSpell.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
      )}
    </div>
  ) : null;

  // N7-1: the action row lives UNDER THE OPEN BOOK, not inside the page. It used to sit at the end of
  // the right-hand page's scroll, so a member had to scroll the parchment to discover that saving,
  // downloading and the Cauldron existed at all. Out here it is always visible with the book.
  // W1-12: Contents joins this row because the bottom turn bar is GONE (the arrows moved to the book's sides).
  const spellActions = openSpell ? (
    <div className="sb-underbar">
      <div className="sb-actions">
        {/* 14-B6a: the spellbook's underbar keeps its own layout (a 14px check inside the book's
            chrome) and takes its sentences from the one law, as the tarot reader's toolbar does. */}
        <span className="result-dest__shelf"><Check size={14} /> {t(language, "result.onShelf", { room: roomLabel("magic") })}</span>
        <button type="button" className="button button--ghost" onClick={saveToRoom} disabled={save === "saving" || save === "saved"}>{save === "saved" ? t(language, "result.saved") : save === "saving" ? t(language, "result.saving") : t(language, "result.save")}</button>
        <button type="button" className="button button--ghost" onClick={() => downloadReportPdf(spellToReport(openSpell))}>{t(language, "reports.download")}</button>
        <SendToCauldronButton language={language} kind="spell" title={spellShelfName(openSpell)} data={spellToReport(openSpell)} />
        <button type="button" className="sb-turn sb-turn--contents" onClick={() => turnTo(null)}>{t(language, "spell.contents")}</button>
      </div>
      {saveNote ? <p className="find-your-type__status">{saveNote}</p> : null}
    </div>
  ) : null;

  const leftContent = spread === "spell" ? spellLeft : contentsLeft;
  const rightContent = spread === "spell" ? spellRight : contentsRight;

  return (
    <div className="specialty-hero spellbook-hero">
      <p className="eyebrow">Magic Room · The Spellbook</p>
      <h1 className="dream-title">The Conjure Spellbook</h1>
      <p className="spellbook-lede">{t(language, "spell.lede")}</p>

      <div className={`spellbook3d${open ? " is-open" : ""}${reduced ? " is-reduced" : ""}`}>
        {!open ? (
          <button type="button" className="sb-closed" onClick={() => { setOpen(true); setMobilePage("contents"); }} aria-label={t(language, "spell.openAria")}>
            <img className="sb-closed__cover" src={magicTileSpellbookUrl} alt="The Conjure Spellbook" />
            <span className="sb-closed__hint"><BookOpen size={16} /> {t(language, "spell.open")}</span>
          </button>
        ) : (
          <>
            {/* W1-12: ARROWS AT THE SIDES of the book, midway down, always visible (a disabled arrow dims, it
                never disappears), bottom row gone. The stage wrapper is what makes it the BOOK's sides. */}
            <div className="sb-stage">
              <div className={`sb-book sb-book--${mobilePage}`} style={{ backgroundImage: `url(${magicBookOpenUrl})` }}>
                <div className={`sb-leaf${reduced ? "" : " is-turning"}`} key={pageKey}>
                  <div className={`sb-book__page sb-book__page--left${showSide("left") ? " is-shown" : ""}`}>{leftContent}</div>
                  <div className={`sb-book__page sb-book__page--right${showSide("right") ? " is-shown" : ""}`}>{rightContent}</div>
                </div>
              </div>
              <button type="button" className="sb-arrow sb-arrow--prev" onClick={goPrev} disabled={idx <= 0} aria-label={t(language, "spell.turnBack")}><ChevronLeft size={30} /></button>
              <button type="button" className="sb-arrow sb-arrow--next" onClick={goNext} disabled={idx < 0 || idx >= pages.length - 1} aria-label={t(language, "spell.turnForward")}><ChevronRight size={30} /></button>
            </div>
            {spellActions}
            {/* The phone shows ONE page of the spread at a time, so it keeps a toggle. Hidden on desktop. */}
            <button type="button" className="sb-mobile-toggle" onClick={() => setMobilePage(mobilePage === "contents" ? "page" : "contents")}>{t(language, mobilePage === "contents" ? "spell.nextPage" : "spell.prevPage")}</button>
          </>
        )}
      </div>
    </div>
  );
}
